'use client';
import React, { useRef, useState, useEffect } from 'react';
import { ButtonHTMLAttributes, ReactNode } from 'react';

interface LiquidGlassButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: 'primary' | 'outline' | 'glass';
  className?: string;
}

export default function LiquidGlassButton({
  children,
  variant = 'primary',
  className = '',
  ...props
}: LiquidGlassButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const [liquidOffset, setLiquidOffset] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!buttonRef.current) return;
    
    const rect = buttonRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setMousePosition({ x, y });
    
    // Liquid physics effect - creates a flowing movement
    const liquidX = (x / rect.width - 0.5) * 20;
    const liquidY = (y / rect.height - 0.5) * 20;
    setLiquidOffset({ x: liquidX, y: liquidY });
  };

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => {
    setIsHovered(false);
    setLiquidOffset({ x: 0, y: 0 });
  };

  const baseStyles = `
    relative overflow-hidden
    transition-all duration-300 ease-out
    backdrop-blur-xl
    border
    rounded-lg
    font-semibold
    transform
    hover:scale-105
    active:scale-95
  `;

  const variantStyles = {
    primary: `
      bg-gradient-to-br from-white/10 to-white/5
      border-white/20
      text-white
      shadow-lg
      hover:shadow-xl
      hover:border-white/30
    `,
    outline: `
      bg-gradient-to-br from-white/5 to-transparent
      border-white/20
      text-white
      shadow-md
      hover:shadow-lg
      hover:border-white/30
      hover:bg-white/10
    `,
    glass: `
      bg-gradient-to-br from-white/15 to-white/5
      border-white/30
      text-white
      shadow-xl
      hover:shadow-2xl
    `
  };

  return (
    <button
      ref={buttonRef}
      className={`${baseStyles} ${variantStyles[variant]} ${className}`}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        transform: `perspective(1000px) rotateX(${-liquidOffset.y * 0.5}deg) rotateY(${liquidOffset.x * 0.5}deg)`,
      }}
      {...props}
    >
      {/* Light refraction effect */}
      <div
        className="absolute inset-0 pointer-events-none opacity-0 transition-opacity duration-300"
        style={{
          opacity: isHovered ? 0.6 : 0,
          background: `radial-gradient(
            circle at ${mousePosition.x}px ${mousePosition.y}px,
            rgba(255, 255, 255, 0.4) 0%,
            rgba(255, 255, 255, 0.1) 30%,
            transparent 70%
          )`,
        }}
      />

      {/* Liquid ripple effect */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(
            circle at ${mousePosition.x}px ${mousePosition.y}px,
            rgba(255, 255, 255, 0.1) 0%,
            transparent 50%
          )`,
          opacity: isHovered ? 0.8 : 0,
          transition: 'opacity 0.3s ease-out',
        }}
      />

      {/* Animated liquid border */}
      <div
        className="absolute inset-0 pointer-events-none rounded-lg"
        style={{
          background: `linear-gradient(
            ${45 + liquidOffset.x * 2}deg,
            transparent 0%,
            rgba(255, 255, 255, 0.1) 50%,
            transparent 100%
          )`,
          opacity: isHovered ? 1 : 0.5,
          transition: 'opacity 0.3s ease-out',
        }}
      />

      {/* Inner glow */}
      <div
        className="absolute inset-0 pointer-events-none rounded-lg"
        style={{
          boxShadow: `inset 0 0 20px rgba(255, 255, 255, ${isHovered ? 0.1 : 0.05})`,
          transition: 'box-shadow 0.3s ease-out',
        }}
      />

      {/* Content */}
      <span className="relative z-10">{children}</span>
    </button>
  );
}
